import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { useTelegram } from '@/shared/lib/telegram'

export interface EventReminderState {
  enabled: boolean
  delivery: any | null
}

/**
 * Хук для проверки состояния напоминания о событии
 */
export function useEventReminder(eventId: string) {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['event-reminder', eventId],
    queryFn: async (): Promise<EventReminderState> => {
      if (!initData) return { enabled: false, delivery: null }
      
      const res = await apiClient<{ success: boolean; data: any[] }>(
        `/api/user/event-notifications/deliveries?eventId=${eventId}`,
        { initData }
      )
      
      // Напоминание активно, если есть доставка в статусе "queued"
      const active = res.data?.find(d => d.status === 'queued')
      return {
        enabled: !!active,
        delivery: active || null,
      }
    },
    enabled: !!eventId && !!initData,
    staleTime: 15 * 1000, // 15 секунд
  })
}

/**
 * Хук для переключения состояния напоминания о событии
 */
export function useToggleEventReminder(eventId: string) {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (action: 'enable' | 'disable') => {
      if (!initData) throw new Error('Требуется авторизация')

      if (action === 'enable') {
        return apiClient<{ success: boolean }>(`/api/user/events/${eventId}/reminders`, {
          method: 'POST',
          body: JSON.stringify({ leadTimeMinutes: 24 * 60 }), // 24 часа по умолчанию
          initData,
        })
      } else {
        return apiClient<{ success: boolean }>(`/api/user/events/${eventId}/reminders`, {
          method: 'DELETE',
          initData,
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-reminder', eventId] })
    },
  })
}

/**
 * Хук для получения глобальных настроек уведомлений
 */
export function useEventNotificationPreferences() {
  const { initData } = useTelegram()

  return useQuery({
    queryKey: ['event-notification-preferences'],
    queryFn: async () => {
      if (!initData) return { eventReminders: true }
      
      const res = await apiClient<{ success: boolean; data: { eventReminders: boolean } }>(
        '/api/user/event-notifications/preferences',
        { initData }
      )
      return res.data
    },
    enabled: !!initData,
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Хук для изменения глобальных настроек уведомлений
 */
export function useUpdateEventNotificationPreferences() {
  const { initData } = useTelegram()
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (eventReminders: boolean) => {
      if (!initData) throw new Error('Требуется авторизация')
      
      return apiClient<{ success: boolean; data: { eventReminders: boolean } }>(
        '/api/user/event-notifications/preferences',
        {
          method: 'PUT',
          body: JSON.stringify({ eventReminders }),
          initData,
        }
      )
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-notification-preferences'] })
    },
  })
}
